import { faqImages, type FAQImageKey } from "@/assets/faq";

// Shorthand for cleaner data: [key] = inline, [key:block] = block, [key:sm] = small, [key:lgBlock] = large, [key:side] = side-by-side
type ImgRef = FAQImageKey | `${FAQImageKey}:block` | `${FAQImageKey}:sm` | `${FAQImageKey}:lgBlock` | `${FAQImageKey}:side`;

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  images?: ImgRef[]; // Images referenced in order of appearance
  tags?: string[]; // Additional search keywords
}

export interface FAQCategory {
  id: string;
  name: string;
  items: FAQItem[];
}

export interface VideoItem {
  id: string;
  title: string;
  description: string;
  youtubeId: string;
}

// Helper to get image URL from key
export const getImage = (key: FAQImageKey): string => faqImages[key];

// Training videos shown in the Videos tab
export const videoData: VideoItem[] = [
  {
    id: "vid-1",
    title: "Getting Started with Hourglass: Automated Timekeeping Made Easy",
    description: "Learn how to set up and start using Hourglass for automatic time tracking.",
    youtubeId: "DH8WbE5BBLk",
  },
  {
    id: "vid-2",
    title: "Hourglass Tips & Tricks: Optimizing for your Workflow",
    description: "Discover advanced features and best practices to get the most out of Hourglass.",
    youtubeId: "xlXgVLobFyQ",
  },
];

export const faqData: FAQCategory[] = [
  {
    id: "videos",
    name: "Videos",
    items: [], // Videos rendered separately, not as FAQ items
  },
  {
    id: "getting-started",
    name: "Getting Started",
    items: [
      {
        id: "gs-1",
        question: "What's the difference between the Hourglass desktop app and the dashboard?",
        answer: `Hourglass has two parts that work together: the __desktop application__ and the __dashboard__.

**The Desktop Application**
The Hourglass desktop application is installed on your computer and runs quietly in the background. Its only job is to track the work you do on your computer. You generally do not interact with it directly.

You can find it in the bottom right corner of your screen under the [arrow] icon. The Hourglass icon is: [greenIcon]
[sysTrayPopup:block]

**The Dashboard**
The Hourglass dashboard is a web page that you open in Google Chrome or Microsoft Edge. This is where you review, edit, and export your time entries. You are currently viewing the dashboard now.

**Important:** If the desktop application is not running or you are not signed in, Hourglass cannot track time and no entries will appear on the dashboard.`,
      },
      {
        id: "gs-2",
        question: "How do I sign in to Hourglass?",
        answer: `The Hourglass desktop application should already be running on your computer because it starts right when you login. However, to start tracking your time, you must sign into the Hourglass desktop application.

**To sign in:**
1. Click the [arrow] icon in the bottom right corner of your screen
2. Right click the blue H icon [greenIcon]
3. Click __Login__
4. Enter your firm email and password
[loggedOutMenu:block]

Once signed in, Hourglass will begin tracking automatically.

You will know it is working when the blue H icon shows a **green circle** [greenIcon]. If the circle is **red** [redIcon], Hourglass is not tracking and you need to sign in again.

**Tip:** You can pin the Hourglass icon to your taskbar by clicking and dragging the blue H icon out from under the [arrow] and placing it on the taskbar.
[pinIcon:sm]`,
      },
      {
        id: "gs-3",
        question: "How do I know Hourglass is running and tracking my time?",
        answer: `Hourglass is tracking your time when the blue H icon [greenIcon] in the bottom right corner of your screen has a **green circle** next to it.

If the circle is **red** [redIcon], Hourglass is not tracking.

**To sign back in:**
1. Right click the blue H icon [greenIcon]
2. Click __Login__
3. Enter your email and password
[loggedOutMenu:block]

Once signed in, you should see a message confirming that Hourglass is tracking your activity.

You can also confirm tracking by opening the Hourglass dashboard. As you work, time entries will automatically appear on the __Tracked__ page after you switch from one task to another.

If the icon is green [greenIcon] but no time appears, try logging out and logging back in. If the issue continues, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "gs-4",
        question: "How do I start tracking time each day?",
        answer: `In most cases, Hourglass starts automatically when you log into your computer.

Hourglass remembers your previous login and usually signs you in automatically. Occasionally, your login may expire and you will need to sign in again.

Each morning, check the bottom right corner of your screen and confirm the blue H icon has a **green circle** [greenIcon]. If it is **red** [redIcon], you will need to sign in.

**To sign in:**
1. Right click the blue H icon [greenIcon]
2. Click __Login__
3. Enter your email and password
[loggedOutMenu:block]`,
      },
      {
        id: "gs-5",
        question: "What does Hourglass track, and when does it create a time entry?",
        answer: `Hourglass tracks work done in common applications, including:

• Microsoft Word, Outlook, Excel, and PowerPoint
• Outlook Calendar meetings
• Adobe PDF and Kofax PDF
• Google Chrome

As you work, Hourglass creates time entries automatically. Each task gets its own entry. Examples include drafting a document, reviewing a PDF, writing an email, or attending a meeting.

If you return to the same document or email multiple times in a day, Hourglass updates the existing entry instead of creating duplicates.

A time entry is created when you **switch from one task to another**. For example, moving from an email to a Word document. Within a few seconds of switching, the entry will appear on the dashboard.

If you do not switch tasks, Hourglass assumes you are still working and will wait before creating an entry.`,
      },
      {
        id: "gs-6",
        question: "How can I quickly open Hourglass and the dashboard?",
        answer: `You can access Hourglass quickly in a few different ways.

**Save the dashboard for quick access**

   **Option 1:** Bookmark the dashboard in Chrome or Edge
   1. Click the star icon in the URL bar at the top of your browser
   2. Name the bookmark Hourglass
   3. Save it to your Bookmarks bar
[bookmarkSettings:block]

   **Option 2:** Pin the dashboard to your taskbar (Microsoft Edge only)
   1. Click the … icon in the top right corner of Microsoft Edge
[threeDotsEdge:sm]
   2. Select More tools
[moreToolsEdge:block]
   3. Click Pin to taskbar

**Pin the Hourglass desktop application**

Pinning the desktop app makes it easy to confirm that Hourglass is running and tracking.

1. Click the [arrow] icon in the bottom right corner of your Windows taskbar
2. Click and hold the blue H icon [greenIcon]
3. Drag it to the taskbar, to the right of the [arrow] icon
[pinIcon:sm]

We recommend pinning the desktop app so you can quickly see whether Hourglass is signed in and tracking.`,
      },
      {
        id: "gs-7",
        question: "Who do I reach out to for questions, issues, or feature requests?",
        answer: `The best way to contact us is through the **Support** button in the bottom left corner of the Hourglass dashboard. You can use it to report issues, ask questions, or request features.

You can also email dayn@hourglass.law directly.`,
      },
    ],
  },
  {
    id: "daily-use",
    name: "Daily Use",
    items: [
      {
        id: "du-1",
        question: "When are my time entries created, and where do they go?",
        answer: `Hourglass tracks your work in real time and creates time entries automatically.

A time entry is created when you **switch from one task to another**. For example, moving from an email to a Word document, or from a document to a PDF. When you switch, Hourglass finalizes the previous task and immediately begins tracking the new one.

Each entry takes about 1 to 3 seconds to appear and will show up on the __Tracked__ page of the Hourglass dashboard.

The __Tracked__ page works like a drafts folder. Entries here are not final and are not sent to your billing system until you export them.`,
      },
      {
        id: "du-2",
        question: "Can I edit a time entry?",
        answer: `Yes. You can edit all parts of a time entry before exporting it.

To edit an entry, click anywhere on the row in the __Tracked__ page. This opens the edit window.

From there, you can:
• Select or change the matter number
• Update task and activity codes
• Edit the description or narrative

The matter dropdown shows your most recently billed matters, and it improves over time as you use Hourglass. You can also search by matter number, matter name, or client name.

When you are done editing, you have two options:
• **Save changes** — keeps the entry in Tracked so you can edit it later
• **Save and export** — finalizes the entry and sends it to your billing system

Once an entry is exported, it can no longer be edited in Hourglass.`,
      },
      {
        id: "du-3",
        question: "How do I send time entries to my billing system?",
        answer: `You can export time entries in two ways.

**Option 1:** Export multiple entries
1. Select one or more entries using the checkboxes on the left side of the rows
2. Click the **Export** button in the top right corner

**Option 2:** Export from the edit window
1. Open an entry by clicking on it
2. Click **Save and export** when you are done editing

After exporting, the entry is removed from the __Tracked__ page and moved to the __Exported__ page.

The __Exported__ page shows all time you have already sent to your billing system for the selected day.

Once an entry is exported, it cannot be changed in Hourglass. Any further changes must be made directly in your billing system.`,
      },
      {
        id: "du-4",
        question: "How can I delete a time entry?",
        answer: `You can delete one or more time entries from the __Tracked__ page.

1. Select the entries using the checkboxes on the left side
2. Click the **Delete** button in the top right corner
3. Confirm the deletion when prompted

Deleted entries are removed from Hourglass and will not be exported.`,
      },
      {
        id: "du-5",
        question: "How can I add a time entry?",
        answer: `You can add entries from the __Tracked__ page.

1. Click the **Add** button in the toolbar. [addEntryButton:block]
2. The **Add Entry** pop-up will appear. Fill in each field with the information for your new entry. [addEntryEmptyPopup:block]
The **Generate with AI** button can help refine your description. Enter a short explanation and Hourglass will improve it for clarity and billing language.

Once complete, choose one of the following options:
• **Duplicate:** Adds the entry and keeps the form open for similar entries.
• **Add and export:** Adds the entry directly to Exported entries.
• **Add entry:** Adds the entry to Tracked entries.

To edit the added entry, click anywhere on its row to open the **Edit Entry** dialog.`,
      },
      {
        id: "du-6",
        question: "How can I combine multiple time entries?",
        answer: `You can combine multiple entries from the __Tracked__ page.

1. Select the entries you want to combine by clicking the checkbox next to each entry. You can select as many entries as you'd like. [selectMultipleEntries:block]
2. Click the **Combine** button in the toolbar. [selectCombine:block]
3. The selected entries will merge into a single entry. This time entry will have a **Combine** logo (highlighted). [entriesCombined:lgBlock]

• **Narratives:** Automatically combine into one unified narrative.
• **Units:** Reflect the sum of all combined entries.

To edit the combined entry, click anywhere on its row to open the **Edit Entry** dialog.`,
      },
      {
        id: "du-7",
        question: "How can I duplicate a time entry?",
        answer: `You can duplicate a time entry from the __Tracked__ page.

1. Select the entries you wish to duplicate by clicking the checkbox next to the entry. [duplicateEntrySelected:block]
2. Click the **Duplicate** button in the toolbar. [duplicateEntryButton:block]
3. The selected entry will duplicate into a new entry. This time entry will have a **Duplicate** logo (highlighted). [duplicateEntryDuplicated:lgBlock]

To edit the added entry, click anywhere on its row to open the **Edit Entry** dialog.`,
      },
      {
        id: "du-8",
        question: "Can I view and export time from previous days?",
        answer: `Yes. You can view and export time from past days in two ways.

**Option 1:** Use the calendar filter
1. On the __Tracked__ or __Exported__ page, click the calendar icon in the top right corner
2. Select a specific day or date range

**Option 2:** Use the Calendar page
1. Click the __Calendar__ page on the left side of the dashboard
2. View a monthly summary and click into individual days

Once you are viewing a previous day, you can export entries from that day as usual.

If an entry was created today but needs to be billed to a prior date, you can edit the entry and change the date before exporting it.`,
      },
    ],
  },
  {
    id: "troubleshooting",
    name: "Troubleshooting",
    items: [
      {
        id: "ts-1",
        question: "Hourglass is not tracking any of my time. What do I do?",
        answer: `If Hourglass is not tracking your time, start by checking the icon in the bottom right corner of your screen.

Look for the blue H icon [greenIcon] near the clock. If the circle next to it is **red** [redIcon], you are not signed in.

**To sign in:**
1. Right click the blue H icon
2. Click __Login__
3. Enter your firm email and password
[loggedOutMenu:block]

If the icon is **green** [greenIcon] and time is still not appearing, try signing out and signing back in.

If that does not help, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-2",
        question: "I'm signed in, but Hourglass still isn't tracking my work.",
        answer: `If you are signed in and the icon shows a **green circle** [greenIcon], but no entries are appearing, there are a few things to check.

**1. Are you working in a supported application?**
Hourglass tracks activity in:
• Microsoft Word, Outlook, Excel, PowerPoint
• Adobe Acrobat and Kofax PDF
• Google Chrome and Microsoft Edge

If you are working in an application not on this list, Hourglass will not create an entry.

**2. Have you switched tasks?**
Hourglass creates a time entry when you **switch from one task to another**. If you have been working on the same document without switching, the entry may not appear until you move to something else.

**3. Try signing out and back in**
This resets your connection and can resolve many tracking issues.

If the issue continues, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-3",
        question: "Hourglass stopped tracking my work for part of the day. Can I recover that time?",
        answer: `Hourglass can only track time while it is running and signed in. If tracking stopped during part of your day, the time from that period cannot be recovered automatically.

However, you can manually add the missing time.

**To add a manual entry:**
1. Click the **Add** button in the top right corner of the dashboard
2. Fill in the matter, task, activity, and description
3. Enter the start time, end time, or duration
4. Save the entry

To prevent this from happening again, check that the Hourglass icon [greenIcon] has a **green circle** each morning. If it is **red** [redIcon], sign in before starting your work.`,
      },
      {
        id: "ts-4",
        question: "My time looks incorrect or incomplete. What could be causing this?",
        answer: `There are a few reasons why time entries may appear incorrect or incomplete.

**1. Hourglass was not signed in**
If the icon [redIcon] was red during part of your day, that time was not tracked. You can add it manually using the **Add** button.

**2. You worked in an unsupported application**
Hourglass tracks work in Microsoft Office, Adobe PDF, Kofax PDF, and Chrome. Other applications are not tracked.

**3. You did not switch tasks**
Hourglass creates entries when you switch from one task to another. If you worked on the same document for a long time without switching, the entry may not appear until you move to something else.

**4. The entry is still processing**
After switching tasks, it takes a few seconds for the entry to appear. If it does not appear after 10 seconds, try refreshing the dashboard.

If you continue to see incorrect or missing time, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-5",
        question: "I received a notification saying I'm not signed in. How do I sign back in?",
        answer: `If you receive a notification that you are not signed in, Hourglass has stopped tracking your time.

**To sign back in:**
1. Click the [arrow] icon in the bottom right corner of your screen
2. Right click the blue H icon [greenIcon]
3. Click __Login__
4. Enter your firm email and password
[loggedOutMenu:block]

Once signed in, the icon should show a **green circle** [greenIcon] and Hourglass will begin tracking again.

If you are repeatedly getting signed out, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-6",
        question: "All of my time is being assigned to the same matter number. Why?",
        answer: `If all of your entries are being assigned to the same matter, you may have a **hardcoded matter** set in your settings.

The hardcoded matter feature assigns all new entries to a specific matter by default. This can be helpful for attorneys who work primarily on one case, but it overrides automatic predictions.

**To check or turn off a hardcoded matter:**
1. Go to the __Settings__ page
2. Look for the __Hardcoded Matter__ option
3. Clear the field or select a different matter

Once cleared, Hourglass will resume predicting matter numbers based on your work activity.

If you do not have a hardcoded matter set and are still seeing this issue, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-7",
        question: "Some time entries do not have a matter number assigned. How can I fix this?",
        answer: `Hourglass predicts matter numbers based on your recent billing history and the content of your work. If it cannot make a confident prediction, the matter field will be left blank.

This is common when:
• Working on a new matter you have not billed to before
• Working on something that does not clearly match a recent matter
• The document or email does not contain enough identifying information

**To assign a matter:**
1. Click on the entry to open the edit window
2. Use the matter dropdown to search by matter number, matter name, or client name
3. Select the correct matter and save

Over time, as you bill more entries, Hourglass will improve its predictions for your work.`,
      },
      {
        id: "ts-8",
        question: "I edited a time entry, but my changes didn't save. What happened?",
        answer: `If your changes did not save, there are a few possible causes.

**1. You clicked outside the edit window without saving**
Changes are only saved when you click **Save changes** or **Save and export**. Clicking outside the window or pressing Escape will close it without saving.

**2. There was a connection issue**
If your internet connection dropped while saving, the changes may not have gone through. Try editing the entry again.

**3. The entry was already exported**
Once an entry is exported, it cannot be edited in Hourglass. Any changes must be made directly in your billing system.

To confirm whether an entry has been exported, check the __Exported__ page. If the entry appears there, it has already been sent to your billing system.`,
      },
      {
        id: "ts-9",
        question: "I can't add a manual entry even though all fields are filled out.",
        answer: `If the **Save** button is disabled even though all fields appear complete, there may be a validation issue.

**Common causes:**
• **Matter is required** — Make sure a matter is selected from the dropdown, not just typed in
• **Duration is zero or negative** — Check that the start and end times are correct
• **Task or activity code is missing** — Some firms require these fields

Try clearing and re-entering each field. If the issue persists, refresh the page and try again.

If you continue to have trouble, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-10",
        question: "Hourglass is not picking up on certain emails. Why?",
        answer: `Hourglass tracks emails that you **read, write, or reply to** in Microsoft Outlook on your desktop. However, not all emails are tracked.

**Emails that may not be tracked:**
• Emails opened on your phone or in Outlook Web
• Emails that were only previewed in the reading pane without being opened
• Internal emails (if your firm has disabled internal email tracking)
• Emails in folders that Outlook does not sync locally

**To ensure an email is tracked:**
• Open the email fully by double-clicking it
• Spend enough time reading it for Hourglass to register activity

If you are still not seeing certain emails, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-11",
        question: "Hourglass isn't picking up on PDFs or document review. Why?",
        answer: `Hourglass tracks PDF work in **Adobe Acrobat** and **Kofax Power PDF**. If you are using a different PDF reader, Hourglass may not be able to track that activity.

**Common reasons PDFs are not tracked:**
• Using an unsupported PDF viewer (e.g., browser-based PDF viewer, Foxit, Nitro)
• The PDF was opened inside another application (e.g., embedded in Outlook or a web page)
• The PDF application was not in focus long enough to register

**To ensure PDF work is tracked:**
• Open PDFs in Adobe Acrobat or Kofax Power PDF
• Make sure the PDF window is in the foreground while you work

If you are using a supported application and still not seeing entries, contact your IT Support Desk or email dayn@hourglass.law.`,
      },
      {
        id: "ts-12",
        question: "I already exported a time entry, but I need to change it. What can I do?",
        answer: `Once a time entry has been exported, it cannot be edited in Hourglass. The entry has already been sent to your firm's billing system.

**To make changes:**
You will need to edit the entry directly in your billing system (such as Aderant, Elite, ProLaw, or Clio).

If you do not have access to edit entries in your billing system, contact your billing coordinator or IT Support Desk for assistance.

**Tip:** Before exporting, review your entries carefully on the __Tracked__ page. You can edit any field before clicking **Export**.`,
      },
    ],
  },
  {
    id: "settings",
    name: "Settings",
    items: [
      {
        id: "st-1",
        question: "Can I make Hourglass track time faster?",
        answer: `Yes. You can adjust how quickly Hourglass creates a time entry.

**By default:**
• Emails require about 20 seconds of total time before an entry is created
• Documents such as Word, PDF, Excel, and PowerPoint require about 30 seconds

This time is aggregated across the day, not just one continuous session.

**To change these thresholds:**
1. Open the __Settings__ tab in the dashboard
2. Find __Minimum time (emails)__ and __Minimum time (docs)__
3. Adjust the number of seconds to your preference

Lower values cause entries to appear faster. Higher values reduce very short entries.`,
      },
      {
        id: "st-2",
        question: "Can I customize how Hourglass writes my time entry narratives?",
        answer: `Yes. Narrative customization is controlled by the **Draft detailed narratives** setting.

When this setting is turned on, Hourglass automatically generates detailed descriptions of your work.

When you turn it off, additional settings appear that let you control how narratives are written.

**The main narrative controls are:**

**Check for red words**
Removes flagged or sensitive words from generated narratives automatically.

**Show task breakdown in combined entries**
When enabled, combined entries list each task separately with its associated time.
When disabled, Hourglass creates a single narrative that summarizes all work performed.

**Max word count**
Controls how long and detailed the narrative is. Higher values produce longer descriptions.

**Practice type**
Tells Hourglass the type of law you practice so narratives match your billing style and terminology.`,
      },
      {
        id: "st-3",
        question: "What does \"Re-track exported entries\" mean?",
        answer: `By default, once you export a time entry, Hourglass assumes that work is finalized for the rest of the day.

For example, if you export time for drafting a motion and then continue working on that same document later in the day, Hourglass will not create additional entries for that work.

If you turn **Re-track exported entries** on, Hourglass will continue tracking work on tasks even after you export earlier entries for the same day.

This is useful if you frequently return to the same document after exporting time.`,
      },
      {
        id: "st-4",
        question: "What does \"Track internal emails\" mean?",
        answer: `By default, Hourglass ignores emails that are only exchanged between employees of your firm.

This helps filter out non-billable internal communication.

If you bill time for internal emails, you can turn this setting on so those emails appear on your dashboard.`,
      },
      {
        id: "st-5",
        question: "Does Hourglass pick up emails sent from my phone?",
        answer: `By default, Hourglass does not track emails sent from a mobile device.

If you enable **Track emails from phone**, Hourglass will begin creating entries for those emails so they appear on your dashboard along with desktop work.`,
      },
      {
        id: "st-6",
        question: "Can I pause or temporarily stop tracking?",
        answer: `Currently, the only way to stop tracking is to quit the Hourglass desktop application.

We generally do not recommend doing this, since the application will not restart automatically.

**If you need to quit Hourglass:**
1. Click the [arrow] icon in the bottom right corner of your taskbar
2. Right click the blue H icon [greenIcon]
3. Click __Quit__

If you are stepping away from your desk, it is usually better to leave your active window on the Hourglass dashboard or your desktop instead of quitting the application.`,
      },
    ],
  },
  {
    id: "tips-tricks",
    name: "Tips & Tricks",
    items: [
      {
        id: "tt-1",
        question: "How can I make Hourglass start and sign me in automatically each morning?",
        answer: `By default, Hourglass is set to start automatically when you log into your computer and will sign you in as long as your login credentials have not expired.

The best way to make sure this works consistently is to pin the Hourglass desktop application to your taskbar and check its status each morning. When the blue H icon shows a **green circle** [greenIcon], Hourglass is signed in and tracking. If it is **red** [redIcon], you will need to sign back in.

Making this quick check part of your daily routine helps prevent missed time.`,
      },
      {
        id: "tt-2",
        question: "How can I help Hourglass predict the correct matter number more often?",
        answer: `Hourglass relies on context from your work to predict the correct matter number.

For documents such as Word, PDF, Excel, and PowerPoint files, Hourglass first looks at the folder path. Documents that are saved in the correct matter folder are much more likely to be assigned correctly. Hourglass also looks at the document title for a matter number or case name.

For emails, Hourglass looks primarily at the subject line. If the subject line does not include a matter number, case name, or claim number, the matter may be left blank.

**You can improve matter prediction by:**
• Including the matter number or case name in email subject lines
• Filing documents in the correct matter folder before working on them
• Using the hardcoded matter feature when you know you will be working on a single matter for an extended period of time

If you use the hardcoded matter feature, remember to turn it off when you switch to other work so entries are not assigned incorrectly.`,
      },
      {
        id: "tt-3",
        question: "How can Hourglass capture document review done on printed materials?",
        answer: `If you are reviewing documents on paper, Hourglass can still capture the time.

Before reviewing the printed document, open the electronic version of the document on your computer and click into it so it is the active window. While you review the paper copy, leave that document open.

When you are finished, switch to another task on your computer. Hourglass will then create a time entry for the document review. You may need to slightly edit the narrative to reflect that the review was done on a printed copy.`,
      },
      {
        id: "tt-4",
        question: "How can I prevent Hourglass from tracking time when I step away from my computer?",
        answer: `Hourglass does not automatically detect when you step away from your desk. It continues tracking the task you were last working on.

Before stepping away, make sure you are not clicked into a billable document, email, or PDF. The simplest way to do this is to leave your active window on the Hourglass dashboard or on your computer's desktop.

This ensures Hourglass does not continue tracking billable work while you are away.`,
      },
      {
        id: "tt-5",
        question: "How can I ensure Hourglass accurately tracks time when working on multiple monitors?",
        answer: `Hourglass tracks work based on which application and window your mouse is actively clicked into.

When working across multiple monitors, make sure to click into the window you are actively working on. Hourglass detects task changes when your mouse clicks into a new application or window.

A common issue occurs when users read a document on a second monitor without clicking into it. Hourglass cannot follow eye movement, so it will not know you are working in that document unless you click into it.`,
      },
      {
        id: "tt-6",
        question: "Hourglass picked up multiple entries for one task. How can I combine them?",
        answer: `This can happen when you move between applications for a single task, such as switching between Word, email, and a PDF while responding to a question.

Instead of deleting entries and manually adjusting time, you can combine them.

1. Select the related entries using the checkboxes on the left side
2. Click the **Combine** button in the top right corner of the dashboard

If Hourglass is unsure which matter to assign to the combined entry, it will prompt you to select one. You can adjust how combining works in the __Settings__ if needed.`,
      },
      {
        id: "tt-7",
        question: "How can I efficiently review and sort my time in the Hourglass dashboard?",
        answer: `Hourglass works best when you review your time throughout the day rather than all at once at the end.

If you are reviewing entries later, there are a few tools that help.

The **Title** column helps identify what you were working on. For documents, this shows the document title. For emails, it shows the subject line. If the Title column is hidden due to screen size, you can also see this information when editing an entry.

You can also sort entries by matter number by clicking the **Matter** column header on the __Tracked__ page. This groups related entries together and makes review faster.`,
      },
      {
        id: "tt-8",
        question: "How can I track work in applications that Hourglass does not directly integrate with?",
        answer: `For applications that Hourglass does not track automatically, you can use the manual tools built into the dashboard.

You can manually add time using the **Add** button or use the built-in timer to track work as it happens. Once the timer is stopped, you will be prompted to fill in the required fields.

This ensures all billable work is captured, even outside supported applications.`,
      },
      {
        id: "tt-9",
        question: "What habits help Hourglass work best throughout the day?",
        answer: `A few simple habits can significantly improve accuracy and reduce review time.

• Pin the Hourglass desktop application to your taskbar and check that it is signed in each morning
• Keep the Hourglass dashboard open on one monitor so you can review entries as they appear
• Include matter numbers or case names in document titles and email subject lines when possible
• Delete irrelevant or non-billable entries as you go
• Use the **Title** field to quickly understand what each entry refers to
• Manually add time for work that Hourglass cannot automatically track`,
      },
    ],
  },
  {
    id: "technical-details",
    name: "Technical Details",
    items: [
      {
        id: "td-1",
        question: "How does Hourglass track my time?",
        answer: `Hourglass tracks time by monitoring which application and window you are actively working in on your computer.

**As you work, Hourglass:**
• Identifies the active application or document you are clicked into
• Collects limited work context from supported applications so it can understand what you are working on
• Tracks time until you switch to a different task
• Creates a time entry when you switch tasks

Hourglass detects task changes based on normal computer activity, such as clicking into a different application or document.

By default, Hourglass bills time in 0.1 increments. This can be adjusted in your __Settings__.`,
      },
      {
        id: "td-2",
        question: "How does Hourglass predict the matter number?",
        answer: `Hourglass uses multiple methods to accurately predict the matter number for each time entry.

**Important Note:** Only matters with active status are used for predictions. Closed or pending matters are excluded.

**Method 1: Exact Match**
Hourglass first scans the entry title for direct matches to:
• Matter number
• Case number
• Claim number

**Method 2: Name Matching**
If no exact match is found, Hourglass searches for last names in the entry title. When a name is found, the software identifies all active cases containing that name and determines the most likely match. If no confident match can be made, the matter field is left blank to avoid inaccurate predictions.

**When No Prediction Is Made**
If neither method produces a confident result, the **Matter** field will remain blank in your Tracked entries. In these cases, you'll need to edit the entry and select the matter manually.

Have ideas to improve our predictions? Navigate to the __Support__ tab and select **Feature Request** to share your feedback.`,
      },
      {
        id: "td-3",
        question: "Does Hourglass record my screen or keystrokes?",
        answer: `No. Hourglass does not record your screen, monitor your keystrokes, or track what you type.

Hourglass only monitors which application you are actively working in and, for certain supported applications, temporarily reads limited work information to generate a time entry.

For example, when working in Microsoft Office or supported PDF applications, Hourglass may temporarily access:
• The file name and file path
• The document contents needed to generate a billing narrative
• The amount of time spent working

This information is used only to create a time entry and is not recorded or stored long term.`,
      },
      {
        id: "td-4",
        question: "Can my firm see what I'm typing, reading, or working on?",
        answer: `No. Your firm cannot see what you are typing, reading, or working on in real time.

Hourglass only shows your firm the time entries that you choose to export to your billing system. Anything that appears in your __Tracked__ dashboard remains private to you.

Work context collected to generate a time entry is deleted after the entry is created and is never visible to your firm.`,
      },
      {
        id: "td-5",
        question: "What information does Hourglass collect, and what does it not collect?",
        answer: `Hourglass temporarily collects limited work context so it can generate accurate time entries.

**This includes information such as:**
• Application name
• File name and file path
• Document or email content needed to write a narrative
• Time spent on each task

Once a time entry is created, this work context is deleted and not stored.

**Hourglass does not collect:**
• Screen recordings
• Keystrokes
• Personal browsing outside supported workflows
• Any activity when the desktop application is not running`,
      },
      {
        id: "td-6",
        question: "Do I need to have the Hourglass dashboard open for it to track time?",
        answer: `No. You do not need to keep the dashboard open.

As long as the Hourglass desktop application is running and you are signed in, Hourglass will track your work in the background. The dashboard is only used to review, edit, and export time entries.`,
      },
      {
        id: "td-7",
        question: "Does Hourglass slow down my computer?",
        answer: `No. Hourglass is designed to be lightweight and non-invasive.

It runs quietly in the background and should not cause noticeable performance issues or slowdowns while you work.

If you ever experience performance problems that you believe may be related to Hourglass, please contact your IT Support Desk or the Hourglass team so we can investigate.`,
      },
      {
        id: "td-8",
        question: "What happens if I'm working offline or lose internet access?",
        answer: `If you lose internet access, Hourglass will not be able to track or process time entries during that period.

Currently, Hourglass requires an active internet connection to function properly. If you anticipate working offline, you may need to manually add time once you reconnect.

We are actively working on improvements to better handle offline work in the future.`,
      },
    ],
  },
];
